import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, IndianRupee, Calendar, User, FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { SetRevenueTargetModalProps, RevenueTarget } from '@/types/revenue';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const SetRevenueTargetModal: React.FC<SetRevenueTargetModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  existingTarget
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    userId: '',
    month: '',
    target: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch users for dropdown (CEO/GROUP_MANAGER only)
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const result = await response.json();
      return result.data || [];
    },
    enabled: isOpen && ['CEO', 'GROUP_MANAGER'].includes(user?.role || '')
  });

  useEffect(() => {
    if (existingTarget) {
      setFormData({
        userId: existingTarget.userId || '',
        month: existingTarget.month,
        target: existingTarget.target.toString(),
        notes: existingTarget.notes || ''
      });
    } else {
      // Default to current month
      const currentMonth = new Date().toISOString().slice(0, 7);
      setFormData({
        userId: '',
        month: currentMonth,
        target: '',
        notes: ''
      });
    }
  }, [existingTarget, isOpen]);

  const createTargetMutation = useMutation({
    mutationFn: async (data: Partial<RevenueTarget>) => {
      const token = localStorage.getItem('token');
      const url = existingTarget
        ? `${API_URL}/revenue-targets/${existingTarget.id}`
        : `${API_URL}/revenue-targets`;
      const method = existingTarget ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save revenue target');
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: `Revenue target ${existingTarget ? 'updated' : 'created'} successfully`
      });
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.month) {
      newErrors.month = 'Month is required';
    } else {
      const isValidMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(formData.month);
      if (!isValidMonth) {
        newErrors.month = 'Month must be in YYYY-MM format';
      }
    }

    if (!formData.target || parseFloat(formData.target) <= 0) {
      newErrors.target = 'Target amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data: Partial<RevenueTarget> = {
      userId: formData.userId || undefined,
      month: formData.month,
      target: parseFloat(formData.target),
      notes: formData.notes || undefined
    };

    createTargetMutation.mutate(data);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Set Revenue Target">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">
              {existingTarget ? 'Edit' : 'Set'} Revenue Target
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* User Selection (CEO/GROUP_MANAGER only) */}
            {['CEO', 'GROUP_MANAGER'].includes(user?.role || '') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-2" />
                  Target For
                </label>
                <Select
                  value={formData.userId}
                  onValueChange={(value) => setFormData({ ...formData, userId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Organization-wide (all users)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Organization-wide</SelectItem>
                    {users?.map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Month Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-2" />
                Month <span className="text-red-500">*</span>
              </label>
              <Input
                type="month"
                name="month"
                value={formData.month}
                onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                className={errors.month ? 'border-red-500' : ''}
              />
              {errors.month && (
                <p className="text-red-500 text-sm mt-1">{errors.month}</p>
              )}
            </div>

            {/* Target Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <IndianRupee className="h-4 w-4 inline mr-2" />
                Target Amount (INR) <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="0"
                step="1000"
                name="target"
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                placeholder="e.g., 1000000"
                className={errors.target ? 'border-red-500' : ''}
              />
              {errors.target && (
                <p className="text-red-500 text-sm mt-1">{errors.target}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="h-4 w-4 inline mr-2" />
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional context or goals..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTargetMutation.isPending}
                className="flex-1"
              >
                {createTargetMutation.isPending
                  ? 'Saving...'
                  : existingTarget
                  ? 'Update'
                  : 'Create'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
